import {
  type ComponentProps,
  createContext,
  type MouseEvent,
  type ReactNode,
  useContext,
} from 'react';

type Navigate = (to: string) => void;

const NavigationContext = createContext<Navigate | null>(null);

/**
 * Macht Klicks auf interne Links zu Router-Navigation (ohne Neuladen). Die Bausteine kennen den Router nicht;
 * ohne Anbieter bleiben es gewöhnliche Links.
 */
export function NavigationProvider({
  navigate,
  children,
}: {
  navigate: Navigate;
  children: ReactNode;
}) {
  return <NavigationContext.Provider value={navigate}>{children}</NavigationContext.Provider>;
}

/** Interner Pfad: genau ein führendes `/`, kein `//` (kein Wechsel der Adresse) und kein Schema. */
const INTERNAL_PATH = /^\/(?!\/)[^\s\\]*$/;

export function isInternalPath(href: string): boolean {
  return INTERNAL_PATH.test(href);
}

type InternalLinkProps = Omit<ComponentProps<'a'>, 'href'> & {
  href: string;
  /** Markiert das aktive Ziel (`aria-current="page"`). */
  current?: boolean;
};

const isPlainLeftClick = (event: MouseEvent): boolean =>
  event.button === 0 && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey;

/** Link auf eine Seite dieser Anwendung. Externe Adressen sind ein Programmierfehler. */
export function InternalLink({ href, current = false, onClick, ...props }: InternalLinkProps) {
  const navigate = useContext(NavigationContext);
  if (!isInternalPath(href)) {
    throw new Error(
      `InternalLink erwartet einen Pfad dieser Anwendung (interner Pfad), nicht "${href}"`,
    );
  }
  return (
    <a
      href={href}
      {...(current ? { 'aria-current': 'page' as const } : {})}
      onClick={(event) => {
        onClick?.(event);
        if (event.defaultPrevented || navigate === null || !isPlainLeftClick(event)) {
          return;
        }
        event.preventDefault();
        navigate(href);
      }}
      {...props}
    />
  );
}
